import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconCheckCircle, IconEdit, IconPlus, IconReceipt, IconTrash } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { fmtMoney } from '../../../common/utils/money';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import ClientFarmSearch from '../../clients/forms/search';
import { useSalesInfinite, useDeleteSale } from '../contexts/sales';
import SaleForm from '../forms/sale-form';
import ReconcileModal from '../forms/reconcile-modal';
import PaymentsPanel from '../forms/payments-panel';
import SaleRowActions from './row-actions';

import '../../../common/styles/catalog.css';
import '../styles/sales.css';

// Negocios · Ventas (api-reference/admin-sales.md). Venta POLIMÓRFICA (0058): una cría
// real o una reserva de cruce planeado. Lista filtrable por comprador y estado; alta/
// edición en modal; las reservas pendientes se reconcilian a la cría al nacer.

const STATUS_LABEL = { 2: 'Activa', 1: 'Inactiva', 0: 'Anulada' };
const KIND_LABEL = { real: 'Directa', project_pending: 'Reserva', reconciled: 'Reconciliada' };

const targetName = (s) => (
    s.calf ? (s.calf.name || s.calf.number || `#${s.calf.id}`)
        : s.projected_cross ? (s.projected_cross.code || s.projected_cross.name || `#${s.projected_cross.id}`)
            : '—'
);

export default function SalesOverview() {
    const [params, setParams] = useSearchParams();
    const urlBuyerId = params.get('buyer_id') || '';
    const urlStatus = params.get('status') || '';

    const [buyer, setBuyer] = useState(null);         // solo el label del chip; el filtro vive en la URL (buyer_id)
    const [form, setForm] = useState(null);          // null | 'new' | sale(edit)
    const [reconcile, setReconcile] = useState(null); // venta a reconciliar
    const [payments, setPayments] = useState(null);   // venta cuyos abonos se gestionan

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };
    const clearFilters = () => { setBuyer(null); setParams(new URLSearchParams(), { replace: true }); };

    const { mutateAsync: remove, isPending: removing } = useDeleteSale();
    const confirm = useConfirm();

    const filters = useMemo(() => {
        const out = {};
        if (urlBuyerId) out.buyer = urlBuyerId;
        if (urlStatus) out.status = urlStatus;
        return out;
    }, [urlBuyerId, urlStatus]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useSalesInfinite(filters);
    const sales = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? sales.length;
    const hasFilters = !!(urlBuyerId || urlStatus);

    const anular = async (sale) => {
        const ok = await confirm({
            title: 'Anular venta',
            message: `¿Anular la venta de "${targetName(sale)}"? Queda inactiva; para revender registrá una nueva.`,
            confirmLabel: 'Anular',
            danger: true,
        });
        if (!ok) return;
        try { await remove(sale.id); toast.success('Venta anulada'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo anular'); }
    };

    return (
        <div className="cat-overview">
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Ventas</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="clients" action="write">
                    <button type="button" className="sale-overview__new" onClick={() => setForm('new')}>
                        <IconPlus /> <span>Registrar venta</span>
                    </button>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="sale-overview__filter">
                    {urlBuyerId ? (
                        <span className="sale-overview__chip">
                            <IconCheckCircle /> {buyer?.name || `Comprador #${urlBuyerId}`}
                            <button type="button" className="sale-overview__chip-clear" onClick={() => { setBuyer(null); setParam('buyer_id', ''); }} aria-label="Quitar filtro de comprador">×</button>
                        </span>
                    ) : (
                        <ClientFarmSearch scope="clients" placeholder="Filtrar por comprador…" onSelect={(c) => { setBuyer(c); setParam('buyer_id', String(c.id)); }} />
                    )}
                </div>

                <select className="cat-overview__filter" value={urlStatus} onChange={(e) => setParam('status', e.target.value)} aria-label="Filtrar por estado">
                    <option value="">Activas</option>
                    <option value="0">Anuladas</option>
                </select>

                {hasFilters && <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>}
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver las ventas.' : (error.message || 'No se pudieron cargar las ventas')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando ventas…</div>
            ) : !sales.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">{hasFilters ? 'Ninguna venta coincide con el filtro.' : 'Aún no hay ventas registradas.'}</p>
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Objeto</th><th>Tipo</th><th>Comprador</th><th>Vendedor</th><th>Fecha</th><th>Valor pactado</th><th>Abonado</th><th>Estado</th><th aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((s) => (
                                <tr key={s.id} className="cat-table__row">
                                    <td>
                                        <span className="cat-table__name">{targetName(s)}</span>
                                        {s.calf?.number && s.calf?.name && <span className="sale-table__muted"> · {s.calf.number}</span>}
                                        {!s.calf && s.projected_cross && <span className="sale-kind sale-kind--cross">cruce</span>}
                                        {(s.is_overdue || s.needs_recalculation) && (
                                            <div className="sale-flags">
                                                {s.is_overdue && <span className="sale-flag sale-flag--overdue" title="La próxima fecha de vencimiento ya pasó">Atrasada</span>}
                                                {s.needs_recalculation && <span className="sale-flag sale-flag--recalc" title="Se registró un abono: el vendedor debe recalcular la cuota y guardar">Requiere recálculo</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td>{s.kind ? <span className={`sale-kind sale-kind--${s.kind}`}>{KIND_LABEL[s.kind] || s.kind}</span> : <span className="cat-table__empty">—</span>}</td>
                                    <td>{s.buyer?.name || (s.buyer?.id ? <span className="cat-table__empty">#{s.buyer.id}</span> : <span className="cat-table__empty">—</span>)}</td>
                                    <td>{s.sold_by?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{s.date ? formatDateOnly(s.date) : <span className="cat-table__empty">—</span>}</td>
                                    <td>{s.agreed_value != null ? fmtMoney(s.agreed_value) : <span className="cat-table__empty">—</span>}</td>
                                    <td>
                                        {s.amount_paid ? fmtMoney(s.amount_paid) : <span className="cat-table__empty">—</span>}
                                        {s.installments_count ? <span className="sale-table__muted"> · {s.installments_count} cuotas</span> : null}
                                    </td>
                                    <td><span className={`cat-badge cat-badge--${s.status}`}>{STATUS_LABEL[s.status] || '—'}</span></td>
                                    <td>
                                        <Gate domain="clients" action="write">
                                            <div className="sale-table__tools">
                                                <SaleRowActions disabled={removing}>
                                                    {s.kind === 'project_pending' && s.status !== 0 && (
                                                        <button type="button" role="menuitem" className="sale-menu__item" onClick={() => setReconcile(s)} disabled={removing}><IconCheckCircle /> Reconciliar</button>
                                                    )}
                                                    <button type="button" role="menuitem" className="sale-menu__item" onClick={() => setPayments(s)} disabled={removing}><IconReceipt /> Abonos</button>
                                                    <button type="button" role="menuitem" className="sale-menu__item" onClick={() => setForm(s)} disabled={removing}><IconEdit /> Editar</button>
                                                    {s.status !== 0 && (
                                                        <button type="button" role="menuitem" className="sale-menu__item sale-menu__item--warn" onClick={() => anular(s)} disabled={removing}><IconTrash /> Anular</button>
                                                    )}
                                                </SaleRowActions>
                                            </div>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && sales.length > 0 && (
                <LoadMore
                    shown={sales.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun="ventas"
                />
            )}

            {form && (
                <SaleForm sale={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />
            )}
            {reconcile && (
                <ReconcileModal sale={reconcile} onClose={() => setReconcile(null)} onSaved={() => setReconcile(null)} />
            )}
            {payments && (
                <PaymentsPanel sale={payments} onClose={() => setPayments(null)} />
            )}
        </div>
    );
}
